import React, { useEffect, useState } from "react";
import { io } from "socket.io-client";
import beep from "./beep2.mp3"; // Notification sound file
import toast, { Toaster } from "react-hot-toast";
import { Link } from "react-router-dom";

// Initialize socket connection to the backend
const socket = io(process.env.REACT_APP_BACKEND_URL);

const MessageNotification = ({ userId }) => {
  // State for storing the notification data
  const [notification, setNotification] = useState(null);
  // State to control modal visibility
  const [isModalOpen, setIsModalOpen] = useState(false);
  // State for Do Not Disturb mode (stored in sessionStorage)
  const [doNotDisturb, setDoNotDisturb] = useState(
    sessionStorage.getItem("doNotDisturb") === "true"
  );

  useEffect(() => {
    // Join the user's private room for notifications
    socket.emit("join", userId);

    // Listen for incoming message notifications
    socket.on("message_notification", (data) => {
      // Only show notification if Do Not Disturb is off
      if (!doNotDisturb) {
        setNotification(data);
        setIsModalOpen(true);
        triggerFeedback(); // Trigger vibration and sound

        // Show toast notification
        toast.success(`You have a new message from ${capitalizeWords(data.sender_name)}`);
      }

      // Helper function to capitalize names properly
      function capitalizeWords(name) {
        return name
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(" ");
      }
    });

    // Clean up socket listener when component unmounts
    return () => {
      socket.off("message_notification");
    };
  }, [userId, doNotDisturb]);

  /**
   * Triggers haptic feedback (vibration) and plays notification sound
   */
  const triggerFeedback = () => {
    // Check if vibration API is supported by browser
    if (navigator.vibrate) {
      navigator.vibrate(2000);
    }
    
    // Play notification sound
    const audio = new Audio(beep);
    audio.play().catch((err) => console.error("Error playing sound:", err));
  };

  /**
   * Toggles Do Not Disturb mode on/off
   */
  const toggleDoNotDisturb = () => {
    const newDNDState = !doNotDisturb;
    setDoNotDisturb(newDNDState);
    // Persist the setting in sessionStorage
    sessionStorage.setItem("doNotDisturb", newDNDState);

    // Show appropriate toast notification
    if (newDNDState) {
      toast("Do Not Disturb mode activated. You won't receive modal popups.", {
        icon: "🔕",
        style: { backgroundColor: "#f1c40f", color: "#fff" },
      });
    } else {
      toast.success("Do Not Disturb mode deactivated. You will now receive notifications.");
    }
  }

  return (
    <>
      <Toaster />
      {/* Notification Modal */}
      {isModalOpen && !doNotDisturb && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white mx-4 p-4 max-w-sm w-full text-xs rounded animate__animated animate__tada">
            {/* Message icon */}
            <div className="flex items-center justify-center mb-4">
              <i className="fa-regular fa-message text-green-500 text-4xl"></i>
            </div>
            
            {/* Notification title */}
            <h3 className="font-bold text-center text-gray-800">Message Notification</h3>
            
            {/* Notification message */}
            <p className="text-center text-gray-600 mt-2">
              {notification?.message || "New Message"}
            </p>
            
            {/* Action buttons */}
            <div className="mt-4 flex justify-center gap-4">
              {/* Close button */}
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                Close
              </button>
              
              {/* Toggle DND mode button */}
              <button
                onClick={toggleDoNotDisturb}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400"
              >
                {doNotDisturb ? "Allow Notifications" : "DND"}
              </button>
              
              {/* Link to chat */}
              <Link
                to={`/chat/${notification.sender_id}`}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                Read
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MessageNotification;